import { AccountManager } from "../AccountManagers/types";
import { Proposal } from "../Proposals/types";
import {
  Address,
  ContextNames,
  SearchArgs,
  SharePointDocument,
  User,
} from "../types";

export interface Dealer {
  id: number;
  draftDealerId?: number;
  name: string;
  accountManager: AccountManager;
  accountManagerId: string;
  isSuspended: boolean;
  hasRecentActivity?: boolean;
  needsEsignForced?: boolean;
  needsPricingEsign?: boolean;
  isConcierge?: boolean;
  url?: string;
  email: string;
  telephone: string;
  mobile: string;
  fax: string;
  ccaNumber: string;
  vatNumber: string;
  ccaExpiry?: string;
  established?: string;
  staticMapUrl?: string;
  googleMapsUrl?: string;
  proposals: {
    pageInfo: {
      hasMorePages: boolean;
    };
    edges: {
      node: Proposal;
    }[];
  };
  groups?: {
    id: string;
    name: string;
  }[];
  address: Address;
  bank: DealerBank;
  contacts: DealerContact[];
  FORMSTATE_contactsError?: string;
  location?: {
    latitude: number;
    longitude: number;
  };
  countOfActiveProposals?: number;
  totalFinance?: number;
  mostRecentProposalDate?: string;
  contractSigned?: boolean;
  approvedDate?: string;

  companyType?: string;
  companiesHouseUrl?: string;
  companyRegNumber?: string;
  tradingStyle?: string;
  assetClass?: string;
  fCAStatus?: string;
  fCAUrl?: string;
  // sellAtRate?: number;
  netWorthFromCompaniesHouse?: number;

  questionnaire: DealerSetupQuestionnaire;
  FORMSTATE_requiresQuestionnaire?: boolean;

  averageBalance?: number;
  monthlyVolume?: number;

  rateBands?: DealerRateBand[];
  autoConvert?: boolean;
  isOnICORegister?: boolean;
  allProofsProvided?: boolean;
  icoNumber?: string;
  agreedApr?: number;
  previousApr?: number;
  isMannIslandDealer?: boolean;
  isMannIslandZList?: boolean;
  isRateForRisk?: boolean;
  autoConvertReference?: string;
  mainContact?: DealerContact;
  bonafidee?: DealerBonafideeEsign;
  franchised: boolean;
  isMultiQuote: boolean;
  stockFunding: boolean;
  dealerSetupV2?: boolean;
  pricing: DealerPricing[];
  documentStorageRef?: string;
  setupStatus?: number;
  siteVisit: boolean;
  noSiteVisitReason?: string;
  stockFunder?: string;
  stockFundValue?: number;
  subPrimeComm?: number;
  pricingEffective?: string;
  directDealKey?: string;
  ePricing: boolean;
  iAR: boolean;
}

export interface HouseDealer {
  accountManagerId?: string;
}

export interface DealerRateBand {
  id?: string;
  minVehicleAgeInMonths?: number;
  maxVehicleAgeInMonths?: number;
  flatRate?: number;
  maxFlatRate?: number;
  volumeBonusFlatRate?: number;
}

export interface DealerPricing {
  lender?: string;
  lenderid?: number;
  apr?: number;
  commission?: number;
  asmoney?: number;
  israte4risk?: number;
}

export interface DealerSetupQuestionnaire {
  /** Does the dealer have a previous relationship with EVC? */
  hasPreviousRelationship?: boolean;
  /** If previous relationship with EVC, when was it closed? */
  datePreviousRelationshipClosed?: string;
  /** If previous relationship closed, what was the reason? */
  reasonPreviousRelationshipClosed?: string;
  /** Do the reasons that the previous relationship was closed present problems for future dealing? */
  issuesWithDealingAgain?: boolean;
  /** If previous relationship with EVC, did we experience high levels of writeoffs and/or arrears? */
  highWriteOffsOrArrears?: boolean;
  /** If previous relationship with EVC, is the dealer on any lender Z list? */
  isOnLenderZList?: boolean;
  /** Have any dealership employees had a previous relationship with EVC? */
  employeeRelationshipWithEVC?: boolean;
  /** If employee relationship with EVC, what dealership did the employee previously work for? */
  employeeRelationshipPreviousDealer?: string;
  /** If employee relationship with EVC, how was their previous dealer's relationship with EVC? */
  employeeRelationshipPreviousDealerRelationship?: string;
  /** Additional information about previous relationship with EVC */
  relationshipAdditionalInformation?: string;
  /** Does the dealer own or possess a long term lease on the premises? */
  longTermLeaseOnPremises?: boolean;
  /** Type of ownership or lease */
  longTermLeaseOnPremisesDetails?: string;
  /** How many vehicles does the dealership stock? */
  numberOfVehiclesInStock?: boolean;
  /** Stock review/profile eg Cat C or D vehicles? */
  stockReview?: string;
  /** Is 90% or more of the dealer's stock financeable? */
  isStockFinanceable?: boolean;
  /** Reason 90% or more of the dealer's stock is not financeable */
  reasonNotFinanceable?: string;
  /** Is the mileage of vehicles sold within lender policy? */
  isMileageWithinLenderPolicy?: boolean;
  /** Highest vehicle mileage */
  highestVehicleMileage?: number;
  /** Is age of vehicles sold within underwriting policy? */
  isAgeWithinUnderwritingPolicy?: boolean;
  /** Highest vehicle age */
  highestVehicleAge?: number;
  /** Does the dealer rely on 'SOR' stock? */
  sorStock?: boolean;
  /** Are the vehicles well prepared and reasonably priced? */
  smartAndReasonable?: boolean;
  /** Does the dealer have a forecourt and full signage? */
  hasForecourtAndSignage?: boolean;
  /** Reason the dealer does not have a forecourt and/or signage */
  reasonNoForecourtOrSignage?: string;
  /** Are vehicles photographed consistently on the dealer website? */
  vehiclesPhotographedConsistently?: boolean;
  /** Does the dealership heavily advertise subprime finance? */
  advertisesSubprimeFinance?: boolean;
  /** Can customers visit the dealership freely? i.e not 'by appointment only' */
  customersFreeToVisit?: boolean;
  /** Does the dealership have a customer complaints process? */
  hasComplaintsProcess?: boolean;
  /** Details of the customer complaints process */
  complaintsProcessDetails?: string;
  /** Do you have any reputation concerns? */
  reputationConcerns?: boolean;
  /** Are there a high proportion of negative reviews when searching for the dealer on the web? */
  negativeReviewsOnWeb?: boolean;
  /** Details of negative reviews of the dealer on the web */
  negativeReviewsOnWebDetails?: string;
  /** Are there any negative reviews on social media about the dealership? */
  negativeReviewsOnSocialMedia?: boolean;
  /** Details of negative reviews of the dealer on social media */
  negativeReviewsOnSocialMediaDetails?: string;
  /** Are there negative news stories or google results about the dealer? */
  negativeNews?: boolean;
  /** Details of negative news stories */
  negativeNewsDetails?: string;
  /** Does the dealer have a good reputation within the local trade? */
  hasGoodReputation?: boolean;
  /** Was the dealer referred from a local dealer? */
  wasReferred?: boolean;
  /** Name of other dealer which referred this dealer */
  referredBy?: string;
  /** Are you confident the dealer will represent the Eurodrive brand in a positive and appropriate manner? */
  positiveAndAppropriate?: boolean;
  /** Is the director(s) associated with any other companies? */
  directorsAssociatedWithOtherCompanies?: boolean;
  /** Name of the other company the directors are associated with */
  directorOtherCompanyName?: string;
  /** Link to companies house for the other company the director is associated with */
  directorOtherCompanyUrl?: string;
  /** Company registration number for the other company the director is associated with */
  directorOtherCompanyRegNumber?: string;

  /** Who are the dealerships ultimate beneficial owners? */
  ultimateBeneficialOwners?: string;
  /** Who are the dealership's approved persons? */
  approvedPersons?: string;
  /** Who are the dealership's stock owners / investors? */
  investors?: string;
  /** Who are the dealership's other interested parties? */
  otherInterestedParties?: string;
  /** Who are the dealership's current or previous finance providers? */
  financeProviders?: string;
  /** How long has the dealership dealt with these providers? */
  financeProvidersDuration?: string;
  /** Does the dealership have visual showroom finance offerings? */
  hasVisualShowroomFinance?: boolean;
  /** Why does the dealership want to work with EVC? */
  whyDoesTheDealerWantToWorkWithEVC?: string;

  /** Does the dealer offer PCP loans */
  productSetupPCP?: boolean;
  /** Does the dealer offer LTV loans */
  productSetupLTVLoans?: boolean;
  /** Does the dealer offer subprime loans */
  productSetupSubprime?: boolean;
  /** Does the dealer offer a VAT assist */
  productSetupVATAssist?: boolean;
  /** Does the dealer offer a 0% interest subsidy */
  productSetupZeroPercentSubsidy?: boolean;
  /** List of lenders the dealer is setup with */
  productSetupLenders?: string[];
}

export interface DraftDealerEsignSearchArgs extends SearchArgs {
  draftDealerId?: string;
}

export interface DealerSearchArgs extends SearchArgs {
  accountManagerId?: string;
  regionalSalesManagerId?: string;
  includeSuspended?: boolean;
  fundedDealersOnly?: boolean;
  hasActivityInLastSixWeeks?: boolean;
  hasActivityInLastYear?: boolean;
  hasActiveProposalsOnly?: boolean;
  locationNotFoundOnly?: boolean;
  hasLocationOnly?: boolean;
  hasProposalsPaidOutInTimePeriod?: boolean;
  proposalStartDate?: string;
  proposalEndDate?: string;
  autoConvertOnly?: boolean;
  notInAutoConvertOnly?: boolean;
  houseDealersOnly?: boolean;
  excludeAnnouncementId?: number;
}

export interface DealerProps {
  loggedInUser: User;
  dealer: Dealer;
  context?: ContextNames;
}

export interface DealerContactProps {
  loggedInUser: User;
  contact: DealerContact;
  context?: ContextNames;
}

export interface DealerContact {
  id?: number;
  dealerId?: number;
  title?: string;
  forename?: string;
  surname?: string;
  position?: string;
  telephone?: string;
  mobile?: string;
  fax?: string;
  email?: string;
  notes?: string;
  hasLogin?: boolean;
  emailInclude?: DealerContactEmailInclude;
  isDirector?: boolean;
  directorDateOfBirth?: string;
  isFCARegulatedPerson?: boolean;
  homeAddress: Address;
  isMainContact?: boolean;
  requestsDealerPortalUserAccount?: boolean;
  isSAFTested?: boolean;
  sMS?: boolean;
  directorCheck: boolean;
  noDirectorCheckReason?: string;
}

export enum DealerContactEmailInclude {
  NONE = "NONE",
  TO = "TO",
  CC = "CC",
}

export interface DealerContactSearchArgs extends SearchArgs {
  dealerId?: number;
  accountManagerId?: string;
  regionalSalesManagerId?: string;
}

export interface DealerBank {
  id?: number;
  bank: string;
  accountNumber: string;
  sortCode: string;
  accountName: string;
  bankAddress?: string;
  FORMSTATE_invalidBankAccount?: { accountNumber: string; sortCode: string };
}

export interface AgreePricing {
  dealerId: number;
  pricingAgreedBy: string;
}

export interface ApproveDealer {
  dealerId: number;
  contractSigned: boolean;
  termsAgreedBy: string;
}

export interface IComplianceApproval {
  dealerId: number;
  complianceApprovedBy: string;
}

export enum DealerMapType {
  LOCATIONS = "LOCATIONS",
  PROPOSAL_PAIDOUT_VALUE = "PROPOSAL_PAIDOUT_VALUE",
}

export enum DealerMapStyle {
  DARK = "DARK",
  LIGHT = "LIGHT",
  SATELLITE = "SATELLITE",
}

export interface DraftDealer {
  id?: number;
  accountManagerId: string;
  name?: string;
  address?: string;
  dealerJson: string;
  touchedFields: string[];
  currentSection?: string;
  created?: string;
  updated?: string;
  accountManager?: {
    id: string;
    name: string;
  };
}

export interface DealerEsignature {
  did: number;
  docType?: number;
  remoteAddr?: string;
  notes?: string;
  confirmed?: Date;
  username?: string;
  forename?: string;
  surname?: string;
}

export interface DraftDealerProps {
  loggedInUser: User;
  draftDealer: DraftDealer;
  context?: ContextNames;
}

export interface DraftDealerSearchArgs extends SearchArgs {
  accountManagerId?: string;
  regionalSalesManagerId?: string;
}

export enum DealerDocType {
  SEARCH_CONSENT = 1,
  TERMS_AND_CONDITIONS = 2,
  PRICING_SCHEDULE = 3,
}

export enum DealerDocTypeTitle {
  SEARCH_CONSENT = "Search Consent",
  TERMS_AND_CONDITIONS = "Terms & Conditions",
  PRICING_SCHEDULE = "Pricing Schedule",
}

export enum DealerProofType {
  DIRECTOR_ID = "Director ID",
  COPY_INVOICE = "Copy Invoice",
  BANK_ACCOUNT = "Bank Account",
  DEALER_IMAGES = "Dealer Image",
}

export enum DealerSetupStatus {
  PROCESS_BEGINS = "Process begins",
  SEARCH_CONSENT = "Search consent",
  PRICING_AGREED = "Pricing agreed",
  COMPLIANCE_APPROVED = "Compliance approved",
  DEALER_SIGNED = "Dealer signature",
}

/*
export const DealerProofTypeIcon = new Map<string, IconDefinition>([
  [DealerProofType.DIRECTOR_ID, faUser],
  [DealerProofType.COPY_INVOICE, faShoppingCart],
  [DealerProofType.BANK_ACCOUNT, faMoneyBill],
  [DealerProofType.DEALER_IMAGES, faCamera],
]);
*/

export enum DealerFormSectionName {
  ACCOUNT_MANAGER = "ACCOUNT_MANAGER",
  DEALER_DETAILS = "DEALER_DETAILS",
  COMPANY_DETAILS = "COMPANY_DETAILS",
  BANK_DETAILS = "BANK_DETAILS",
  CREATE_QUESTIONNAIRE = "CREATE_QUESTIONNAIRE",
  HISTORIC_CHECK = "HISTORIC_CHECK",
  DEALER_INFORMATION = "DEALER_INFORMATION",
  CURRENT_SITUATION = "CURRENT_SITUATION",
  PRODUCT_MIX = "PRODUCT_MIX",
  CONTACTS = "CONTACTS",
  SUMMARY = "SUMMARY",
  DEALER_PRICING = "DEALER_PRICING",
  LENDER_PACKAGES = "LENDER_PACKAGES",
  DEALER_PROOFS = "DEALER_PROOFS",
}

export interface DealerProofDocument extends SharePointDocument {}

export interface DealerProofResponse {
  uniqueId: string;
  dealerProofRef: string;
  rootUrl: string;
  rootPath: string;
  documents: DealerProofDocument[];
}

export interface DealerBonafideeEsign {
  id: string;
  autoConvertReference?: string;
  created: Date;
  esignatureSessionReference: string;
  payload?: string;
  status?: number;
  sharepointLink?: string;
  active: boolean;
}

export interface LenderRatesForDealersProps {
  loggedInUser: User;
  rates: LenderRatesForDealers;
  context?: ContextNames;
}

export interface LenderRatesForDealers {
  id: number;
  dealerId: number;
  lenderId: number;
  dateFrom?: Date;
  dateTo?: Date;
  rate?: number;
  commission?: number;
  asMoney?: boolean;
  isRate4Risk?: boolean;
  lender?: {
    id: number;
    name: string;
  };
}

export interface LenderRatesForDealersSearchArgs extends SearchArgs {
  dealerId?: number;
}

export default () => ({});
